import React, { useEffect, useState } from "react";

const card =
  "bg-neutral-900/90 p-6 rounded-2xl shadow-2xl border border-amber-700/30 flex flex-col min-h-[720px]";

const buttonPrimary =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-white font-medium hover:bg-amber-700 transition";

const buttonSecondary =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-amber-600 px-5 py-3 text-amber-300 hover:bg-amber-600/10 transition";

const bookCover =
  "w-36 sm:w-40 rounded-lg border border-amber-700/40 shadow-xl object-cover shrink-0 hover:scale-105 transition-transform duration-300";

const ROADS_COVER = "/images/books/roads-cover.jpg";
const BORROWED_EARTH_COVER = "/images/books/borrowed-earth-cover.jpg";
const QUIET_SHIFT_BANNER =
  "/images/philosophy/the-quiet-shift-banner.webp";
const QUIET_SHIFT_EPISODE_ONE_BANNER =
  "/images/philosophy/inhabiting-the-world-we-inherit.webp";
const BORROWED_GROUND_BANNER =
  "/images/philosophy/borrowed-ground-banner.webp";
const CHESTER_MINERVA_BANNER =
  "/images/philosophy/chester-s-minerva-shrine-banner.webp";
const INHERITANCE_LAND_BANNER =
  "/images/philosophy/the-inheritance-of-the-land-wide.webp";

const QUIET_SHIFT_URL =
  "https://open.spotify.com/episode/22Sfy65kJagXyLCUWH5Ux6?si=scGhkW3kTQqRZRsn0Mkowg";
const QUIET_SHIFT_EMBED_URL =
  "https://open.spotify.com/embed/episode/22Sfy65kJagXyLCUWH5Ux6?utm_source=generator";

const BORROWED_GROUND_URL =
  "https://open.spotify.com/episode/3AIuFE7whgHV1n2g5NDq5R?si=mrCUHpNBStC8GNHfvBw9Mw";
const BORROWED_GROUND_EMBED_URL =
  "https://open.spotify.com/embed/episode/3AIuFE7whgHV1n2g5NDq5R?utm_source=generator";

const CHESTER_MINERVA_URL =
  "https://open.spotify.com/episode/2RVVgo9WpveYTyh3bpmw6h?si=o6DjMkl5S3OpQKsZmnUtnA";
const CHESTER_MINERVA_EMBED_URL =
  "https://open.spotify.com/embed/episode/2RVVgo9WpveYTyh3bpmw6h?utm_source=generator";

const INHERITANCE_LAND_URL =
  "https://open.spotify.com/episode/3LqEnxYn3ACvy8w5kcm3tC?si=dhUSt20QQVSNRW9woaGfTQ";
const INHERITANCE_LAND_EMBED_URL =
  "https://open.spotify.com/embed/episode/3LqEnxYn3ACvy8w5kcm3tC?utm_source=generator";

const podcastEpisodes = [
  {
    number: "Episode Four",
    status: "Latest episode",
    title: "The Inheritance of the Land",
    themes: "Land · Memory · Belonging · Responsibility",
    banner: INHERITANCE_LAND_BANNER,
    alt: "The Inheritance of the Land — episode four of The Quiet Shift",
    url: INHERITANCE_LAND_URL,
    embedUrl: INHERITANCE_LAND_EMBED_URL,
    description: (
      <>
        <em>The Inheritance of the Land</em> reflects on what it means to receive
        a landscape already marked by other lives — roads, boundaries, names,
        ruins and memories — and whether inheritance brings with it a
        responsibility not simply to possess the land, but to notice it.
      </>
    ),
    reflection:
      "The episode widens the lens from particular places to the land itself, asking what we owe to ground shaped by generations before us and what it might mean to carry that inheritance forward with attention rather than ownership.",
  },
  {
    number: "Episode Three",
    status: "Earlier episode",
    title: "Chester’s Minerva Shrine",
    banner: CHESTER_MINERVA_BANNER,
    alt: "Chester’s Minerva Shrine — episode three of The Quiet Shift",
    url: CHESTER_MINERVA_URL,
    embedUrl: CHESTER_MINERVA_EMBED_URL,
    exploreUrl: "/philosophy/minerva-shrine",
    description: (
      <>
        <em>Chester’s Minerva Shrine</em> draws on the chapter of the same name in Roads, Ruins and Traces, reflecting on a Roman carving that has remained in the same sandstone quarry face for nearly two thousand years while roads, bridges, languages and generations changed around it.
      </>
    ),
    reflection:
      "From legionaries and pilgrims to civil war, air raids and modern traffic, the episode explores continuity, erosion and memory through one unsettling fact: Minerva has not moved. We have.",
  },
  {
    number: "Episode Two",
    status: "Earlier episode",
    title: "Borrowed Ground",
    banner: BORROWED_GROUND_BANNER,
    alt: "Borrowed Ground — episode two of The Quiet Shift",
    url: BORROWED_GROUND_URL,
    embedUrl: BORROWED_GROUND_EMBED_URL,
    description: (
      <>
        <em>Borrowed Ground</em> moves through three chapters from{" "}
        <em>Roads, Ruins and Traces</em>—“Counting in Generations”,
        “The Wood Above the Spring” and “The Valley Before the Road”—to
        explore the land across the glacial deep past, the layered human
        centuries and the quietly mysterious present.
      </>
    ),
    reflection:
      "From hollow ways deepened by generations of feet to a spring where silence gathers, the landscape becomes an active participant—teaching, directing, absorbing and remembering.",
  },
  {
    number: "Episode One",
    status: "Begin here",
    title: "Inhabiting the World We Inherit",
    banner: QUIET_SHIFT_EPISODE_ONE_BANNER,
    alt: "Inhabiting the World We Inherit — episode one of The Quiet Shift",
    url: QUIET_SHIFT_URL,
    embedUrl: QUIET_SHIFT_EMBED_URL,
    description:
      "What does it mean to inhabit a landscape rather than simply pass through it? The opening episode reflects on the ancient ground beneath us, the altered skies above us and the quiet environmental changes that can disappear into everyday life.",
    reflection:
      "Drawing on Roads, Ruins and Traces and Borrowed Earth, it is an invitation to pause, look more closely and consider how attention might turn passing observers into thoughtful caretakers.",
  },
];

// ---------------------
// FILE LISTS
// ---------------------

const borrowedEarthFiles = [
  "01_Prologue.md",
  "02_A_Way_of_Arrival.md",
  "03_The_Permission_That_Holds.md",
  "04_The_Long_Making_of_Life.md",
  "05_Interlude__The_Unchosen_Paths.md",
  "06_The_Interior_World.md",
  "07_Marked_with_Ruin.md",
  "08_The_Unseen_Consequences.md",
  "09_The_Shape_of_Imbalance.md",
  "10_The_Perfection_in_Adaptation.md",
  "11_The_Unbidden_Earth.md",
  "12_The_Sky_That_Can_Burn_Us.md",
  "13_The_Fire_Beneath.md",
  "14_The_Slowing_of_the_Ocean.md",
  "15_The_Ice_That_Comes_and_Goes.md",
  "16_The_Earth_Beyond_Us.md",
  "17_A_Different_Kind_of_Uncertainty.md",
  "18_Where_This_Leaves_Us.md",
  "19_A_World_Without_Us.md",
  "20_The_Burden_of_Knowing.md",
  "21_We_Have_Been_Given_Something_Extraordinary.md",
  "22_A_Grain_of_Sand.md",
  "23_For_Those_Yet_to_Walk_Here.md",
  "24_Valediction__A_Way_to_Stand.md",
];

const roadsFiles = [
  "01_Prologue.md",
  "02_Foreword__Why_I_Had_to_Write_This.md",
  "03_Interlude.md",
  "04_Deva__Origin_of_Negotiation.md",
  "05_Deva_Amphitheatre.md",
  "06_Chester_s_Minerva_Shrine.md",
  "07_The_Line_Beyond_the_Rampart.md",
  "08_The_First_Breathing_Space.md",
  "09_Between_Ridge_and_Floodplain.md",
  "10_Counting_in_Generations.md",
  "11_Ashton_Hayes__Saxon_Charter.md",
  "12_Measure__Yield__and_Continuity.md",
  "13_The_First_Rise__Kelsall_Hill.md",
  "14_Stones_at_the_Pass.md",
  "15_Church_Brough__Inscription_at_the_Edge.md",
  "16_Carlisle__Water_That_Refused_to_Stop.md",
  "17_Return_to_Cheshire.md",
  "18_Interlude__Boundary_Ritual.md",
  "19_Kelsborrow.md",
  "20_The_Ring_of_Hills.md",
  "21_Looking_Back_from_the_Escarpment.md",
  "22_Nettleford_Wood__The_Road_That_Would_Not_Vanish.md",
  "23_Nettleford_Wood__Reflections_at_the_Cutting.md",
  "24_A_Road_Is_Never_Just_a_Road.md",
  "25_Before_the_Roman_Line.md",
  "26_The_Wood_Above_the_Spring.md",
  "27_The_Valley_Before_the_Road.md",
  "28_When_the_Forest_Closed.md",
  "29_Lines_Drawn__Lines_Removed.md",
  "30_Wind_Across_Chalk.md",
  "31_Where_the_Lines_Are_Drawn.md",
  "32_At_the_Edge_of_the_Field.md",
  "33_The_Field_That_Feeds.md",
  "34_The_Wood_That_Holds.md",
  "35_What_Was_Lost__What_Was_Gained.md",
  "36_A_Third_Way_in_the_Margin.md",
  "37_The_Ground_Beneath_the_Argument.md",
  "38_Stewardship_and_Continuity.md",
  "39_The_Blood_That_Remains.md",
  "40_The_Quarrymen_s_Cottages.md",
  "41_The_Slow_Work_of_Elements.md",
  "42_Weathering_in_Time.md",
  "43_Gorse_and_Warmth.md",
  "44_Weather_in_the_Body.md",
  "45_Floodplain_Light.md",
  "46_Stone_and_Water.md",
  "47_The_Partly_Revealed.md",
  "48_Modern_Pressure.md",
  "49_Invitation_Rather_Than_Warning.md",
  "50_Under_Altered_Skies.md",
  "51_Valediction__Your_Own_Way_of_Seeing.md",
];

// The page route can change to /quiet-shift without moving these existing
// markdown folders. Keeping the fetch path avoids unnecessary file moves.
function useMarkdownLoader(folder, files, index) {
  const [content, setContent] = useState("");

  useEffect(() => {
    const file = files[index];
    if (!file) return;

    fetch(`/philosophy/${folder}/${file}`)
      .then((res) => res.text())
      .then((text) => setContent(text))
      .catch(() => setContent("Content not found."));
  }, [folder, files, index]);

  return content;
}

function PodcastEpisode({ episode }) {
  return (
    <article className="overflow-hidden rounded-3xl border border-amber-700/30 bg-neutral-900/90 shadow-2xl">
      <img
        src={episode.banner}
        alt={episode.alt}
        className="w-full aspect-[21/9] object-cover"
        loading="lazy"
      />

      <div className="p-6 sm:p-8 lg:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_380px] lg:items-center">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-amber-400">
                {episode.number}
              </p>
              <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-200">
                {episode.status}
              </span>
            </div>

            <h3 className="mt-4 text-3xl font-semibold leading-tight text-amber-100 sm:text-4xl">
              {episode.title}
            </h3>

            {episode.themes ? (
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">
                {episode.themes}
              </p>
            ) : null}

            <p className="mt-5 max-w-3xl leading-7 text-neutral-300">
              {episode.description}
            </p>

            <p className="mt-4 max-w-3xl leading-7 text-neutral-400">
              {episode.reflection}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href={episode.url}
                target="_blank"
                rel="noreferrer"
                className={buttonPrimary}
                aria-label={`Listen to ${episode.title} on Spotify`}
              >
                Listen on Spotify
                <span aria-hidden="true">↗</span>
              </a>

              {episode.exploreUrl ? (
                <>
                  <span
                    aria-hidden="true"
                    className="hidden text-amber-500/60 sm:inline"
                  >
                    •
                  </span>
                  <a
                    href={episode.exploreUrl}
                    className={buttonSecondary}
                    aria-label={`Watch videos and explore ${episode.title}`}
                  >
                    <span aria-hidden="true">▶</span>
                    Watch &amp; Explore
                    <span aria-hidden="true">→</span>
                  </a>
                </>
              ) : null}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl bg-black shadow-xl">
            <iframe
              title={`Spotify player for ${episode.title}`}
              src={episode.embedUrl}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </article>
  );
}

export default function PhilosophyLayer() {
  const [earthIndex, setEarthIndex] = useState(0);
  const [roadsIndex, setRoadsIndex] = useState(0);

  const earthContent = useMarkdownLoader(
    "borrowed-earth",
    borrowedEarthFiles,
    earthIndex
  );

  const roadsContent = useMarkdownLoader(
    "roads-ruins-traces",
    roadsFiles,
    roadsIndex
  );

  return (
    <main className="min-h-screen bg-[#080a0d] text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0d1722] to-[#080a0d] px-4 pb-12 pt-8 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="overflow-hidden rounded-3xl border border-amber-200/20 bg-black shadow-2xl">
            <img
              src={QUIET_SHIFT_BANNER}
              alt="The Quiet Shift: Inheritance and the Art of Noticing"
              className="w-full aspect-[3/1] object-cover"
            />
          </div>

          <div className="mt-5 flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.035] px-5 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm uppercase tracking-[0.22em] text-amber-200">
              Exploring the philosophy behind Roman Storyworld
            </p>

            <nav
              aria-label="The Quiet Shift sections"
              className="flex flex-wrap gap-2 text-sm"
            >
              <a href="#listen" className={buttonSecondary}>
                Listen
              </a>
              <a href="#read" className={buttonSecondary}>
                Read
              </a>
              <a href="#about-quiet-shift" className={buttonSecondary}>
                About
              </a>
            </nav>
          </div>

          <div className="mx-auto max-w-4xl pb-2 pt-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-400">
              Landscape · Memory · Deep Time · Stewardship
            </p>
            <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight text-amber-100 sm:text-5xl lg:text-6xl">
              A place for walking slowly, looking closely and noticing what
              the modern world teaches us to overlook.
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-neutral-300">
              The Quiet Shift brings together writing and conversations about
              the ground beneath us, the skies above us and the fragile world
              we have inherited. It asks how attention might change the way we
              live within landscapes older than any road, boundary or name.
            </p>
          </div>
        </div>
      </section>

      <section
        id="listen"
        className="scroll-mt-24 px-4 py-16 sm:px-6"
        aria-labelledby="listen-heading"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
              Listen
            </p>
            <h2
              id="listen-heading"
              className="mt-3 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl"
            >
              The podcasts
            </h2>
            <p className="mt-4 text-lg leading-8 text-neutral-400">
              Reflections on landscape, memory, inheritance and deep time.
            </p>
          </div>

          <div className="space-y-10">
            {podcastEpisodes.map((episode) => (
              <PodcastEpisode key={episode.title} episode={episode} />
            ))}
          </div>
        </div>
      </section>

      <section
        id="read"
        className="scroll-mt-24 border-y border-white/10 bg-white/[0.025] px-4 py-16 sm:px-6"
        aria-labelledby="read-heading"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
              Read
            </p>
            <h2
              id="read-heading"
              className="mt-3 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl"
            >
              The books behind the podcasts
            </h2>
            <p className="mt-4 text-lg leading-8 text-neutral-400">
              The podcast grows from two companion books: one rooted in
              landscape, memory and human passage; the other in inheritance,
              planetary time and our responsibility to what follows us.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <article className={card}>
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">
                    Landscape and memory
                  </p>
                 <h3 className="mt-2 text-2xl font-semibold text-amber-100">
  Roads, Ruins and Traces
</h3>
                </div>

                <img
                  src={ROADS_COVER}
                  alt="Roads, Ruins and Traces book cover"
                  className={bookCover}
                />
              </div>

              <div className="mb-4 text-sm text-amber-400">
                WAY {roadsIndex + 1} OF {roadsFiles.length}
              </div>

              <div className="mb-6 flex-1 whitespace-pre-line text-neutral-300">
                {roadsContent}
              </div>

              <div className="mt-auto flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() =>
                    setRoadsIndex((roadsIndex + 1) % roadsFiles.length)
                  }
                  className={buttonSecondary}
                >
                  Explore the land
                </button>

                <a
                  href="https://www.amazon.co.uk/dp/B0GRS5TJVT"
                  target="_blank"
                  rel="noreferrer"
                  className={buttonPrimary}
                >
                  View on Amazon
                </a>
              </div>
            </article>

            <article className={card}>
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">
                    Inheritance and responsibility
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold text-amber-100">
                    Borrowed Earth
                  </h3>
                </div>

                <img
                  src={BORROWED_EARTH_COVER}
                  alt="Borrowed Earth book cover"
                  className={bookCover}
                />
              </div>

              <div className="mb-4 text-sm text-amber-400">
                WAY {earthIndex + 1} OF {borrowedEarthFiles.length}
              </div>

              <div className="mb-6 flex-1 whitespace-pre-line text-neutral-300">
                {earthContent}
              </div>

              <div className="mt-auto flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() =>
                    setEarthIndex((earthIndex + 1) % borrowedEarthFiles.length)
                  }
                  className={buttonSecondary}
                >
                  Explore ideas
                </button>

                <a
                  href="https://www.amazon.co.uk/dp/B0GTNJLSW7"
                  target="_blank"
                  rel="noreferrer"
                  className={buttonPrimary}
                >
                  View on Amazon
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section
        id="about-quiet-shift"
        className="scroll-mt-24 px-4 py-20 sm:px-6"
        aria-labelledby="about-quiet-shift-heading"
      >
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <blockquote className="rounded-3xl border border-amber-700/30 bg-gradient-to-br from-amber-950/40 to-neutral-900 p-8 font-serif text-3xl leading-snug text-amber-100 shadow-2xl sm:p-10 sm:text-4xl">
            “We do not possess the earth. We pass within it, for a while.”
          </blockquote>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400">
              About the project
            </p>
            <h2
              id="about-quiet-shift-heading"
              className="mt-3 font-serif text-4xl font-semibold text-amber-100 sm:text-5xl"
            >
              The history beneath the present
            </h2>
            <p className="mt-5 text-lg leading-8 text-neutral-300">
              Roman Storyworld asks what remains beneath the modern landscape.
              The Quiet Shift asks how we should live once we begin to notice
              it. One follows evidence, people and history; the other follows
              attention, inheritance and responsibility.
            </p>
            <p className="mt-4 text-lg leading-8 text-neutral-400">
              They are separate voices arising from the same act of looking
              closely.
            </p>

            <a href="/" className={`${buttonSecondary} mt-7`}>
              Return to Roman Storyworld
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
